import { useMemo, useState } from "react";
import { useApp, type EncargoDeliveryInput } from "@/store/AppStore";
import SectionTitle from "@/components/base/SectionTitle";
import EncargoDeliveryModal from "@/pages/admin/encargos/components/EncargoDeliveryModal";
import { buildFunds } from "@/utils/accounting";
import { formatCOP, formatDate } from "@/utils/format";
import type { Encargo, EncargoStatus } from "@/types";

const STATUS_META: Record<EncargoStatus, { label: string; cls: string; icon: string }> = {
  pendiente: { label: "Pendiente", cls: "bg-secondary-500/20 text-secondary-100", icon: "ri-time-line" },
  entregado: { label: "Entregado", cls: "bg-accent-500/20 text-accent-200", icon: "ri-checkbox-circle-line" },
  cancelado: { label: "Cancelado", cls: "bg-primary-500/20 text-primary-100", icon: "ri-close-circle-line" },
};

// Filtros de la vista: por estado del pedido + un filtro especial para los
// pedidos que quedaron entregados SIN tipo de entrega (hay que completarlos).
type EncargoFilter = "todos" | EncargoStatus | "sinTipo";

const FILTERS: { key: EncargoFilter; label: string }[] = [
  { key: "pendiente", label: "Pendientes" },
  { key: "entregado", label: "Entregados" },
  { key: "sinTipo", label: "Sin tipo de entrega" },
  { key: "cancelado", label: "Cancelados" },
  { key: "todos", label: "Todos" },
];

const AdminEncargos = () => {
  const { data, setEncargoStatus, deleteEncargo, deliverEncargo, addPurchaseToStock } = useApp();
  const [filter, setFilter] = useState<EncargoFilter>("pendiente");
  const [deliverFor, setDeliverFor] = useState<Encargo | null>(null);
  const [feedback, setFeedback] = useState("");

  const cajaBalance = useMemo(
    () => buildFunds(data).funds.find((f) => f.key === "caja")?.balance ?? 0,
    [data]
  );

  const counts = useMemo(
    () => ({
      pendiente: data.encargos.filter((e) => e.status === "pendiente").length,
      entregado: data.encargos.filter((e) => e.status === "entregado").length,
      cancelado: data.encargos.filter((e) => e.status === "cancelado").length,
      sinTipo: data.encargos.filter((e) => e.status === "entregado" && !e.deliverMode).length,
    }),
    [data.encargos]
  );

  const list = useMemo(
    () =>
      data.encargos.filter((e) => {
        if (filter === "todos") return true;
        if (filter === "sinTipo") return e.status === "entregado" && !e.deliverMode;
        return e.status === filter;
      }),
    [data.encargos, filter]
  );

  const pendingValue = data.encargos
    .filter((e) => e.status === "pendiente")
    .reduce((a, e) => a + e.unitPrice * e.qty + e.logisticsFee, 0);

  const stats = [
    { label: "Pedidos pendientes", value: String(counts.pendiente), icon: "ri-time-line", tone: "bg-secondary-500/20 text-secondary-100" },
    { label: "Valor pendiente estimado", value: formatCOP(pendingValue), icon: "ri-money-dollar-circle-line", tone: "bg-accent-500/20 text-accent-200" },
    { label: "Entregados", value: String(counts.entregado), icon: "ri-checkbox-circle-line", tone: "bg-primary-500/25 text-accent-200" },
    { label: "Sin tipo de entrega", value: String(counts.sinTipo), icon: "ri-alert-line", tone: "bg-secondary-500/25 text-secondary-100" },
  ];

  const deliverAdvisor = deliverFor
    ? data.distributors.find((d) => d.id === deliverFor.advisorId)
    : undefined;
  const deliverProduct = deliverFor
    ? data.products.find((p) => p.id === deliverFor.productId)
    : undefined;
  const deliverStock = deliverFor
    ? data.inventory.find((i) => i.productId === deliverFor.productId)?.stockCasa ?? 0
    : 0;

  const handleDeliver = async (input: EncargoDeliveryInput) => {
    await deliverEncargo(input);
    setDeliverFor(null);
    setFeedback("Pedido entregado: se actualizó el inventario, la cuenta del asesor y la contabilidad.");
    setTimeout(() => setFeedback(""), 3600);
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Pedidos & Encargos"
        subtitle="Solicitudes hechas por los asesores desde el catálogo. Márcalas como entregadas o cancélalas."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="glass-panel rounded-lg p-4 md:p-5 border-accent-500/25 animate-fade-up">
            <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.tone}`}>
              <i className={`${s.icon} text-xl`} />
            </span>
            <p className="text-[11px] md:text-xs font-medium text-foreground-500 mt-3">{s.label}</p>
            <p className="text-lg md:text-2xl font-heading font-extrabold text-foreground-950 mt-0.5 break-words">
              {s.value}
            </p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-6 mb-3">
        <div className="flex items-center bg-background-200/60 rounded-full p-1 gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-1.5 ${
                filter === f.key
                  ? "bg-primary-500 text-foreground-950"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {f.label}
              {f.key === "sinTipo" && counts.sinTipo > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-accent-500 text-background-50 text-[10px] font-bold">
                  {counts.sinTipo}
                </span>
              )}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-foreground-500">{list.length} pedidos</span>
      </div>

      {feedback && (
        <div className="mb-3 rounded-md glass-panel border-accent-500/30 px-4 py-2.5 flex items-center gap-2 animate-fade-up">
          <i className="ri-checkbox-circle-line text-accent-300" />
          <p className="text-xs text-foreground-800">{feedback}</p>
        </div>
      )}

      {list.length === 0 ? (
        <div className="glass-panel rounded-lg py-12 text-center">
          <span className="mx-auto w-11 h-11 flex items-center justify-center rounded-full bg-background-200/60 text-foreground-500 mb-2">
            <i className="ri-inbox-archive-line text-xl" />
          </span>
          <p className="text-sm text-foreground-500">No hay pedidos en esta vista.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((e) => {
            const meta = STATUS_META[e.status];
            const total = e.unitPrice * e.qty + e.logisticsFee;
            return (
              <div key={e.id} className="glass-panel rounded-lg p-4 animate-fade-up">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <span className="w-10 h-10 rounded-lg bg-primary-500/25 text-accent-200 flex items-center justify-center shrink-0">
                    <i className="ri-inbox-archive-line text-lg" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-bold text-foreground-950">{e.advisorName}</h3>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>
                        <i className={meta.icon} /> {meta.label}
                      </span>
                      <span className="text-[10px] text-foreground-500">{formatDate(e.date)}</span>
                    </div>
                    <p className="text-xs text-foreground-700 mt-1">
                      {e.qty} und · {e.productName}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-foreground-500">
                      <span className="inline-flex items-center gap-1">
                        <i className={e.delivery === "domicilio" ? "ri-truck-line" : "ri-store-2-line"} />
                        {e.delivery === "domicilio" ? "Domicilio" : "Recojo"}
                      </span>
                      {e.logisticsFee > 0 && <span>Fee {formatCOP(e.logisticsFee)}</span>}
                      <span className="font-semibold text-foreground-800">Total {formatCOP(total)}</span>
                    </div>
                    {e.note && (
                      <p className="text-[11px] text-foreground-600 mt-1.5 italic">“{e.note}”</p>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {(e.status !== "entregado" || !e.deliverMode) && (
                      <button
                        onClick={() => setDeliverFor(e)}
                        className="px-3 py-1.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                      >
                        <i className="ri-hand-heart-line" />{" "}
                        {e.status === "entregado" ? "Asignar entrega" : "Entregar"}
                      </button>
                    )}
                    {e.status === "pendiente" && (
                      <button
                        onClick={() => void setEncargoStatus(e.id, "cancelado")}
                        className="px-3 py-1.5 rounded-md border border-background-300/50 text-foreground-600 hover:text-foreground-900 text-[11px] font-semibold whitespace-nowrap cursor-pointer"
                      >
                        Cancelar
                      </button>
                    )}
                    <button
                      onClick={() => void deleteEncargo(e.id)}
                      className="w-8 h-8 inline-flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 hover:bg-background-200/60 cursor-pointer"
                      aria-label="Eliminar pedido"
                      title="Eliminar pedido"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                </div>

                {e.status === "entregado" && e.deliverMode && (
                  <>
                    <div className="mt-3 pt-3 border-t border-background-300/25 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
                        <p className="text-[10px] text-foreground-500">Tipo de entrega</p>
                        <p className="text-xs font-bold text-foreground-950">
                          {e.deliverMode === "compra" ? "Compra (pagada)" : "Consignación"}
                        </p>
                      </div>
                      <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
                        <p className="text-[10px] text-foreground-500">Tu gasto (compra)</p>
                        <p className="text-xs font-bold text-foreground-950">
                          {formatCOP((e.unitCostBase ?? 0) * e.qty)}
                        </p>
                      </div>
                      <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
                        <p className="text-[10px] text-foreground-500">Le cobras al asesor</p>
                        <p className="text-xs font-bold text-foreground-950">
                          {formatCOP((e.unitCostAdvisor ?? 0) * e.qty)}
                        </p>
                      </div>
                      <div className="rounded-md bg-accent-500/10 border border-accent-500/30 px-3 py-2">
                        <p className="text-[10px] text-accent-300">Tu ganancia</p>
                        <p className="text-xs font-bold text-accent-200">
                          {formatCOP(((e.unitCostAdvisor ?? 0) - (e.unitCostBase ?? 0)) * e.qty)}
                        </p>
                      </div>
                    </div>
                    <p className="text-[10px] text-foreground-500 mt-1.5 flex items-center gap-1.5">
                      <i className="ri-percent-line" />
                      {e.unitPrice > 0 && e.unitCostAdvisor != null
                        ? `Descuento asesor ${Math.round(
                            (1 - (e.unitCostAdvisor ?? 0) / e.unitPrice) * 100
                          )}%`
                        : "Entrega registrada"}
                      {e.deliverWallet ? ` · pagado desde ${e.deliverWallet}` : ""}
                    </p>
                  </>
                )}

                {e.status === "entregado" && !e.deliverMode && (
                  <div className="mt-3 pt-3 border-t border-background-300/25 rounded-md bg-secondary-500/10 border border-secondary-500/25 px-3 py-2 flex items-start gap-2">
                    <i className="ri-error-warning-line text-secondary-200 mt-0.5" />
                    <p className="text-[11px] text-foreground-700">
                      Este pedido quedó{" "}
                      <span className="font-semibold text-foreground-950">entregado sin tipo de entrega</span>
                      : no se movió inventario ni se registró el gasto. Usa{" "}
                      <span className="font-semibold text-foreground-950">“Asignar entrega”</span> y elige
                      compra o consignación para aplicarlo.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <EncargoDeliveryModal
        open={deliverFor !== null}
        encargo={deliverFor}
        margin={deliverAdvisor?.margin ?? 25}
        costBase={deliverProduct?.costBase ?? 0}
        pricePublic={deliverProduct?.pricePublic ?? deliverFor?.unitPrice ?? 0}
        stockCasa={deliverStock}
        cajaBalance={cajaBalance}
        onClose={() => setDeliverFor(null)}
        onSubmit={handleDeliver}
        onBuy={(input) => void addPurchaseToStock(input)}
      />
    </div>
  );
};

export default AdminEncargos;